import React, {
  forwardRef,
  MutableRefObject,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { default as Form } from '../../components/connectedForm';
import HeaderBlock from '../../components/connectedForm/HeaderBlock';
import { useHistory } from 'react-router';
import {
  AlertType,
  EmailRecipientTag,
  FilterOperation,
  ICommonEmailRecipientsQueryResult,
  ICommonEmailRecipientsQueryVariables,
  IDeleteEmailPrintPreviewsMutationResult,
  IDeleteEmailPrintPreviewsMutationVariables,
  IEmailPrintPreview,
  IEmailPrintPreviewAggregateQueryResult,
  IEmailPrintPreviewAggregateQueryVariables,
  IEmailPrintPreviewInput,
  IEmailPrintPreviewMutationResult,
  IEmailPrintPreviewMutationVariables,
  IEmailPrintPreviewWorkspaceQueryResult,
  IEmailPrintPreviewWorkspaceQueryVariables,
  IFilterInput,
  ISendEmailPrintPreviewsMutationResult,
  ISendEmailPrintPreviewsMutationVariables,
} from '../../graphql/schema/p4types';
import { useComponentQueryErrorHandling } from '../../utils/parseErrorResponse';
import { FluentTable } from '../../components/view/FluentViewTable';
import {
  Button,
  createTableColumn,
  Divider,
  makeStyles,
  SelectTabData,
  SelectTabEvent,
  Spinner,
  Tab,
  TableCell,
  TableCellLayout,
  TableColumnDefinition,
  TableRowId,
  TabList,
  TabValue,
  ToastTrigger,
  Tooltip,
  useFocusableGroup,
} from '@fluentui/react-components';
import { useValueState } from '../../utils/hooks';
import { QUERY_PAGE_SIZE } from '../../components/FluentWrapper/useQueryForDetailsList';
import { ApolloQueryResult, useApolloClient, useMutation, useQuery } from '@apollo/client';
import EmailPrintPreviewWorkspaceQuery from '../../graphql/queries/company/EmailPrintPreviewWorkspaceQuery.graphql';
import EmailPrintPreviewAggregateQuery from '../../graphql/queries/company/EmailPrintPreviewAggregateQuery.graphql';
import CommonEmailRecipientsQuery from '../../graphql/queries/common/CommonEmailRecipientsQuery.graphql';
import EmailPrintPreviewMutation from '../../graphql/mutations/company/EmailPrintPreviewMutation.graphql';
import SendEmailPrintPreviewsMutation from '../../graphql/mutations/company/SendEmailPrintPreviewsMutation.graphql';
import DeleteEmailPrintPreviewsMutation from '../../graphql/mutations/company/DeleteEmailPrintPreviewsMutation.graphql';
import BlurTextField from '../../components/connectedForm/BlurTextField';
import Dropdown from '../../components/connectedForm/Dropdown';
import { IBlurTextFieldProps } from '../../components/connectedForm/BlurTextField/BlurTextField';
import GridUploader from '../../components/connectedForm/GridUploader';
import {
  AttachRegular,
  Mail16Regular,
  MailCheckmark16Regular,
  MailCheckmarkRegular,
  MailErrorRegular,
  MailRegular,
} from '@fluentui/react-icons';
import { Control, Controller, SubmitHandler, useForm, UseFormWatch } from 'react-hook-form';
import { serviceActions } from '../../state/modules/service';
import getClientMutationId from '../../utils/getClientMutationId';
import normalizeVariables from '../../graphql/schema/normalizeVariables';
import { useDispatch, useSelector } from 'react-redux';
import { ControllerRenderProps } from 'react-hook-form/dist/types/controller';
import { IDropdownOptionRenderer, IFormAction } from '../../@types/entityTypes';
import { useBackgroundMutation } from '../../utils/BackgroundJobs';
import WorkspaceContext, { WorkspaceContextType } from '../../components/Context/WorkspaceContext';
import { IWorkspaceSelectionState } from '../../components/Workspace/useWorkspaceSelection';
import { BluePill } from '../../components/DropdownEditor/CustomOptions';
import titleCase from '../../utils/TitleCase';
import fieldMetaData from '../../graphql/schema/fieldMetaData';
import styled from 'styled-components';
import InputLabel from '../../components/connectedForm/InputLabel';
import InputGroup from '../../components/connectedForm/InputGroup';
import { DangerButton, PrimaryButton, SecondaryButton, SuccessButton } from '../../components/connectedForm/Button';
import InputBlock from '../../components/connectedForm/InputBlock';
import { Maybe } from 'graphql/jsutils/Maybe';
import { p4Tokens } from '../../styles/themes/default/p4FluentTheme';
import { IStatusFlag } from '../../components/connectedForm/HeaderBlock/HeaderBlock';
import { StatusStyle } from '../../components/connectedForm/HeaderStatus';
import { genericRemoteFilterGetter } from '../../components/FluentWrapper/FluentList';
import { IReduxStore } from '../../@types/reduxTypes';
import { ContextualMenuItemType } from '@fluentui/react';
import Wysiwyg, { WysiwygProps } from '../../components/connectedForm/Wysiwyg';
import { useP4ToastController } from '../system/NotificationsArea';

type IEmailPrintPreviewContext = {
  resetSelection: (() => void) | undefined;
  previewRef: MutableRefObject<IPreviewEditRef> | undefined;
};

const EmailPrintPreviewContext = React.createContext<IEmailPrintPreviewContext>({
  resetSelection: undefined,
  previewRef: undefined,
});

const useCellStyles = makeStyles({
  cellWrapper: { width: '100%', display: 'flex', flexWrap: 'wrap' },
});

const useDividerStyles = makeStyles({
  divider: { paddingBottom: '9px' },
});

const CellSpan = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  width: 100%;
`;

const EmailPrintPreviewCell = ({ item }: { item: IEmailPrintPreview }) => {
  const { embeddedView } = useContext(WorkspaceContext);

  const cellStyles = useCellStyles();

  const reportTypePill = useMemo(() => {
    if (embeddedView) {
      //Report type pill only needs to be displayed if not embedded (Report type is known when displayed in a modal).
      return null;
    }

    const reportTypes = fieldMetaData.ReportType.enumValues;
    const reportType = reportTypes.find((fieldMetaReportType) => fieldMetaReportType.value === item.reportType);
    return reportType?.label ? (
      <Tooltip content={reportType.label} relationship="label" positioning={'after'}>
        <BluePill style={{ position: 'absolute', minWidth: '40px', textAlign: 'center', top: '14px', right: 0 }}>
          {reportType.label}
        </BluePill>
      </Tooltip>
    ) : null;
  }, [embeddedView, item.reportType]);

  const customerName = item.customer?.name;
  const supplierName = item.supplier?.name;
  const name = customerName ?? supplierName ?? null;

  const emailTo = item.emailTo && item.emailTo.length > 0 ? item.emailTo.toString() : null;
  const target = name + (emailTo ? ` - ${emailTo}` : '');

  return (
    <div className={cellStyles.cellWrapper} style={{ paddingRight: embeddedView ? undefined : '60px' }}>
      {reportTypePill}
      <Tooltip content={target} relationship="label">
        <CellSpan>{target}</CellSpan>
      </Tooltip>
      {item.emailSubject ? <CellSpan>{item.emailSubject}</CellSpan> : null}
    </div>
  );
};

const EMAIL_PRINT_PREVIEW_COLUMNS: TableColumnDefinition<IEmailPrintPreview>[] = [
  createTableColumn<IEmailPrintPreview>({
    columnId: 'emailSubject',
    renderCell: (item) => {
      return <EmailPrintPreviewCell item={item} />;
    },
  }),
];

const EmailRecipientPill = ({ emailRecipientTag }: { emailRecipientTag: EmailRecipientTag }) => {
  if (!emailRecipientTag) return null;

  switch (emailRecipientTag) {
    case EmailRecipientTag.Primary:
      return (
        <BluePill style={{ marginLeft: '4px', float: 'right', backgroundColor: '#1A237E' }}>
          {titleCase((emailRecipientTag as string).toLowerCase())}
        </BluePill>
      );
    case EmailRecipientTag.Suggested:
      return (
        <BluePill style={{ marginLeft: '4px', float: 'right' }}>
          {titleCase((emailRecipientTag as string).toLowerCase())}
        </BluePill>
      );
  }
  return null;
};

const EmailPreviewOptionRenderer: IDropdownOptionRenderer = ({ option }) => {
  return (
    <span>
      {option.name} - {option.email}
      <EmailRecipientPill emailRecipientTag={option.emailRecipientTag} />
    </span>
  );
};

const CustomField = ({
  inputCols,
  labelCols,
  label,
  children,
}: {
  inputCols?: number;
  labelCols?: number;
  label: string;
  children: ReactNode;
}) => {
  return (
    <InputBlock flexBox={true}>
      <InputLabel label={label} cols={labelCols} />
      <InputGroup flexBox={true} cols={label ? inputCols : 1} withLabel={!!label}>
        {children}
      </InputGroup>
    </InputBlock>
  );
};

const EmailDropdown = ({
  dataid,
  control,
  label,
  'data-testid': dataTestId,
  watch,
}: {
  dataid: string;
  control: Control<IEmailPrintPreview>;
  label: string;
  'data-testid'?: string;
  watch: UseFormWatch<IEmailPrintPreview>;
}) => {
  const customerId = watch('customer.id');
  const supplierId = watch('supplier.id');

  const { data } = useQuery<ICommonEmailRecipientsQueryResult, ICommonEmailRecipientsQueryVariables>(
    CommonEmailRecipientsQuery,
    {
      variables: {
        customerId,
        supplierId,
      },
      skip: !customerId && !supplierId,
    }
  );

  const recipientOptions = useMemo(() => {
    if (!data) return [];
    return [...data.customerRecipients!, ...data.supplierRecipients!].sort((a, b) => {
      if (!a.emailRecipientTag) {
        return 1;
      } else if (!b.emailRecipientTag) {
        return -1;
      }
      return a.emailRecipientTag.localeCompare(b.emailRecipientTag);
    });
  }, [data]);

  const render = useCallback(({ field }) => {
    const onChange = (newValue: Array<any>) => {
      field.onChange(
        newValue?.map((val) => {
          return val.email;
        }) ?? null
      );
    };

    const value =
      field.value?.map((val) => {
        const knownOption = recipientOptions.find((opt) => opt.email === val);
        if (knownOption) {
          return knownOption;
        }

        return { email: val, name: val };
      }) ?? [];

    return (
      <Dropdown
        data-testid={dataTestId ?? field.name}
        options={recipientOptions}
        label={label}
        multi={true}
        creatable={true}
        rawLabel={true}
        labelKey={'name'}
        valueKey={'email'}
        valueRenderer={EmailPreviewOptionRenderer}
        optionRenderer={EmailPreviewOptionRenderer}
        {...field}
        onChange={onChange}
        value={value}
      />
    );
  }, []);

  return <Controller name={dataid} control={control} render={render} />;
};

const EmailTextField = ({
  dataid,
  control,
  'data-testid': dataTestId,
  label,
}: {
  dataid: string;
  control: Control<IEmailPrintPreview>;
  'data-testid'?: string;
  label: string;
} & Omit<IBlurTextFieldProps, keyof ControllerRenderProps>) => {
  const render = useCallback(
    ({ field }) => (
      <BlurTextField data-testid={dataTestId ?? field.name} label={label} {...field} enableOnChange={true} />
    ),
    [dataTestId, label]
  );

  return <Controller name={dataid} control={control} render={render} />;
};

const EmailWysiwygField = ({
  dataid,
  control,
  watch,
}: {
  dataid: string;
  control: Control<IEmailPrintPreview>;
  watch: UseFormWatch<IEmailPrintPreview>;
} & Omit<WysiwygProps, keyof ControllerRenderProps>) => {
  const id = watch('id');
  const concurrencyVersion = watch('concurrencyVersion');
  const value = watch(dataid);

  const defaultValue = useMemo(() => {
    return value;
    // The "default" value for the Wysiwyg needs to be reset each time id/concurrencyVersion change.
    // It should not reset every time value changes, or you can only type 1 character at a time...
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, concurrencyVersion]);

  const render = useCallback(
    ({ field }) => {
      return <Wysiwyg {...field} defaultValue={defaultValue} allowImages={true} allowColorPicker={true} />;
    },
    [defaultValue]
  );

  return <Controller render={render} name={dataid} control={control} />;
};

enum EmailPrintPreviewAction {
  EmailPrintPreviewSendAll = 'EMAIL_PRINT_PREVIEW_SEND_ALL',
  EmailPrintPreviewSendReady = 'EMAIL_PRINT_PREVIEW_SEND_READY',
  EmailPrintPreviewDeleteAll = 'EMAIL_PRINT_PREVIEW_DELETE_ALL',
}

const useEmailPrintPreviewActions = (
  additionalActions?: Array<IFormAction<any>>,
  previewRef: MutableRefObject<{ onSubmit: () => Promise<any> }>
): Array<IFormAction<any>> => {
  const client = useApolloClient();
  const { resetSelection } = useContext(EmailPrintPreviewContext);
  const [sendEmailPrintPreviews] = useBackgroundMutation<
    ISendEmailPrintPreviewsMutationResult,
    ISendEmailPrintPreviewsMutationVariables
  >(SendEmailPrintPreviewsMutation, {
    onCompleteJob: (job) => {
      //Evict emailPrintPreviews - Will need to be refetched so we only see to-be-processed previews in the list.
      client.cache.evict({ fieldName: 'emailPrintPreviews' });
      resetSelection?.();
    },
  });
  const [deleteEmailPrintPreviews] = useBackgroundMutation<
    IDeleteEmailPrintPreviewsMutationResult,
    IDeleteEmailPrintPreviewsMutationVariables
  >(DeleteEmailPrintPreviewsMutation, {
    onCompleteJob: (job) => {
      //Evict emailPrintPreviews - Will need to be refetched so we only see to-be-processed previews in the list.
      client.cache.evict({ fieldName: 'emailPrintPreviews' });
      resetSelection?.();
    },
  });

  const { workspaceFilters } = useContext(WorkspaceContext);

  return useMemo(
    () => [
      {
        key: 'sendApproved',
        text: 'Send Ready Emails',
        buttonProps: {
          iconProps: { iconName: 'save' },
          tooltip: (
            <>
              Send all emails tagged as ready {<MailCheckmark16Regular style={{ color: p4Tokens.statusFlagSuccess }} />}{' '}
              (Including the currently selected email)
            </>
          ),
        },
        onClick: () => {
          previewRef.current.onSubmit().then((value) => {
            sendEmailPrintPreviews({
              variables: {
                input: {
                  clientMutationId: getClientMutationId(),
                  actionCriteria: {
                    filters: [
                      ...workspaceFilters,
                      {
                        filterOperation: FilterOperation.Equals,
                        filterParameters: {
                          field: 'ready',
                          value: 'true',
                        },
                      },
                    ],
                    actionEntities: [],
                    include: false,
                  },
                },
              },
            });
          });
        },
        individualButton: true,
        individualButtonComponent: PrimaryButton,
        actionReference: EmailPrintPreviewAction.EmailPrintPreviewSendReady,
        ignorePermissions: true,
      },
      {
        key: 'sendAll',
        text: 'Send All Emails',
        iconProps: { iconName: 'save', style: { color: 'grey' } },
        onClick: () => {
          previewRef.current.onSubmit().then((value) => {
            sendEmailPrintPreviews({
              variables: {
                input: {
                  clientMutationId: getClientMutationId(),
                  actionCriteria: {
                    filters: workspaceFilters,
                    actionEntities: [],
                    include: false,
                  },
                },
              },
            });
          });
        },
        actionReference: EmailPrintPreviewAction.EmailPrintPreviewSendAll,
        ignorePermissions: true,
        tooltip: (
          <>
            Send all valid emails {<br />}
            (ready {<MailCheckmark16Regular style={{ color: p4Tokens.statusFlagSuccess }} />} or not {<Mail16Regular />}
            )
          </>
        ),
      },
      ...(additionalActions ?? []),
      //Header to create some separate for the "Delete" action to avoid miss-clicks.
      {
        key: 'delete',
        itemType: ContextualMenuItemType.Header,
        text: 'Delete',
        ignorePermissions: true, //Form actions are filtered out if no permission config is set, so need this to bypass the check.
      } as any, //Special case for injecting subheader
      {
        key: 'deleteAll',
        text: 'Delete All Email Previews',
        onClick: () => {
          deleteEmailPrintPreviews({
            variables: {
              input: {
                clientMutationId: getClientMutationId(),
                actionCriteria: {
                  filters: workspaceFilters,
                  actionEntities: [],
                  include: false,
                },
              },
            },
          });
        },
        actionReference: EmailPrintPreviewAction.EmailPrintPreviewDeleteAll,
        ignorePermissions: true,
      },
    ],
    [additionalActions, deleteEmailPrintPreviews, sendEmailPrintPreviews, workspaceFilters]
  );
};

const useOnClickSaveSingle = (id: number, onSubmit: () => Promise<any>) => {
  const { dispatchSimpleToast } = useP4ToastController();

  return () => {
    if (!id) return;

    //Submit should tag as ready (If in a valid state), then send should pick up that it's a ready email and send it.
    onSubmit().then(() => {
      dispatchSimpleToast('Preview Saved', 'The email preview has been saved.', AlertType.Success);
    });
  };
};

const useOnClickSendSingle = (id: number, onSubmit: () => Promise<any>) => {
  const client = useApolloClient();
  const { resetSelection } = useContext(EmailPrintPreviewContext);
  const [sendEmailPrintPreviews] = useBackgroundMutation<
    ISendEmailPrintPreviewsMutationResult,
    ISendEmailPrintPreviewsMutationVariables
  >(SendEmailPrintPreviewsMutation, {
    onCompleteJob: (job) => {
      //Evict emailPrintPreviews - Will need to be refetched so we only see to-be-processed previews in the list.
      client.cache.evict({ fieldName: 'emailPrintPreviews' });
      resetSelection?.();
    },
  });
  const { dispatchSimpleToast } = useP4ToastController();

  return () => {
    if (!id) return;

    const input = {
      clientMutationId: getClientMutationId(),
      actionCriteria: {
        filters: [
          {
            filterOperation: FilterOperation.Equals,
            filterParameters: {
              field: 'id',
              value: id,
            },
          },
        ],
        actionEntities: [],
        include: false,
      },
    };

    //Submit should tag as ready (If in a valid state), then send should pick up that it's a ready email and send it.
    onSubmit().then((submitResult: Maybe<IEmailPrintPreview>) => {
      dispatchSimpleToast('Preview Saved', 'The email preview has been saved.', AlertType.Success);
      sendEmailPrintPreviews({
        variables: {
          input: input,
        },
      });
    });
  };
};

const useOnClickDeleteSingle = (id?: number) => {
  const client = useApolloClient();
  const { resetSelection } = useContext(EmailPrintPreviewContext);
  const [deleteEmailPrintPreviews] = useBackgroundMutation<
    IDeleteEmailPrintPreviewsMutationResult,
    IDeleteEmailPrintPreviewsMutationVariables
  >(DeleteEmailPrintPreviewsMutation, {
    onCompleteJob: (job) => {
      //Evict emailPrintPreviews - Will need to be refetched so we only see to-be-processed previews in the list.
      client.cache.evict({ fieldName: 'emailPrintPreviews' });
      resetSelection?.();
    },
  });

  return () => {
    deleteEmailPrintPreviews({
      variables: {
        input: {
          clientMutationId: getClientMutationId(),
          actionCriteria: {
            filters: [
              {
                filterOperation: FilterOperation.Equals,
                filterParameters: {
                  field: 'id',
                  value: id,
                },
              },
            ],
            actionEntities: [],
            include: false,
          },
        },
      },
    });
  };
};

/**
 * Mock component to display before an email preview has been selected by the user (or after send/delete operations since the selected preview is now invalid).
 */
const PreviewUnselected = () => {
  return (
    <>
      <TabList>
        <Tab icon={<MailRegular />} id="Email" value="email">
          Email
        </Tab>
      </TabList>
      <div style={{ padding: '1em' }}>
        <h4>Select an email preview to begin.</h4>
      </div>
    </>
  );
};

//Generally prefer to avoid refs but the structure of this form makes it difficult to avoid in any sensible manner.
//Need to make the outer form "actions" be able to access the onSubmit/getValues functions of the edit container,
// this is an easy/effective way to achieve that.
type IPreviewEditRef = { onSubmit: () => Promise<any>; getValues: () => IEmailPrintPreview };

/**
 * Right-side view for print-preview - Expecting a minimal (partial) version of the record as a prop, which will be used as an id source to fetch the full record.
 */
const PreviewEdit = forwardRef<IPreviewEditRef, { selectedPreview?: Partial<IEmailPrintPreview> }>(
  ({ selectedPreview }, ref: MutableRefObject<IPreviewEditRef>) => {
    const handleQueryError = useComponentQueryErrorHandling();
    const { dispatchSimpleToast } = useP4ToastController();

    const [selectedTab, setSelectedTab] = React.useState<TabValue>('email');

    const {
      control,
      handleSubmit,
      watch,
      formState: { isDirty },
      reset,
      getValues,
    } = useForm<IEmailPrintPreview>();

    const [updateEmailPrintPreview] = useMutation<
      IEmailPrintPreviewMutationResult,
      IEmailPrintPreviewMutationVariables
    >(EmailPrintPreviewMutation, {
      onCompleted: (data) => {
        const result = data?.emailPrintPreviewUpdate?.result;
        if (result?.id === selectedPreview?.id) {
          reset(result);
        }
      },
      onError: handleQueryError,
      refetchQueries: [EmailPrintPreviewAggregateQuery, EmailPrintPreviewWorkspaceQuery],
    });

    const submit = useCallback<SubmitHandler<IEmailPrintPreview>>(
      (data: IEmailPrintPreview) => {
        return updateEmailPrintPreview({
          variables: {
            input: {
              clientMutationId: getClientMutationId(),
              emailPrintPreview: normalizeVariables<IEmailPrintPreview, IEmailPrintPreviewInput>({
                ...data,
                ready: true,
              }),
            },
          },
        });
      },
      [updateEmailPrintPreview]
    );

    const onSubmit = useMemo(() => handleSubmit(submit), [handleSubmit, submit]);
    ref.current = { onSubmit, getValues };

    useEffect(() => {
      const id = watch('id');
      const ready = watch('ready');
      if (!id) {
        reset(selectedPreview);
      } else if (selectedPreview?.id !== id) {
        const isInvalid = !selectedPreview.emailTo || selectedPreview.emailTo.length === 0;
        if (isDirty || (!ready && !isInvalid)) {
          onSubmit().then((submitResult) => {
            //If this submit was initialised due to the user clicking another row, we can now reset the form data to load that record.
            if (submitResult?.id !== selectedPreview?.id) {
              reset(selectedPreview);
              dispatchSimpleToast(
                'Previous Preview Saved',
                'The previously selected email preview has been saved.',
                AlertType.Success
              );
            }
          });
        } else {
          reset(selectedPreview);
        }
      }
    }, [
      selectedPreview?.id,
      selectedPreview?.concurrencyVersion,
      onSubmit,
      watch,
      isDirty,
      selectedPreview,
      reset,
      dispatchSimpleToast,
    ]);

    const onTabSelect = (event: SelectTabEvent, data: SelectTabData) => {
      setSelectedTab(data.value);
    };

    const onClickSaveSingle = useOnClickSaveSingle(selectedPreview?.id, onSubmit);
    const onClickSendSingle = useOnClickSendSingle(selectedPreview?.id, onSubmit);
    const onClickDeleteSingle = useOnClickDeleteSingle(selectedPreview?.id);

    const styles = useDividerStyles();

    const attachmentsRender = useCallback(({ field }) => {
      const onChange = (newValue) => {
        if (Array.isArray(newValue)) {
          field.onChange(newValue);
        } else if (newValue && newValue.cellKey === 'deleted' && field.value?.length > newValue.rowIdx) {
          //When you delete, we don't get the array, we instead get an object describing the deletion.
          const cut = field.value.slice();
          cut[newValue.rowIdx].deleted = true;
          field.onChange(cut);
        }
      };

      return <GridUploader value={field.value} onChange={onChange} />;
    }, []);

    return (
      <div style={{ borderLeft: '1px solid lightgrey' }}>
        <TabList selectedValue={selectedTab} onTabSelect={onTabSelect}>
          <Tab icon={<MailRegular />} id="Email" value="email">
            Email
          </Tab>
          <Tab icon={<AttachRegular />} id="Attachments" value="attachments">
            Attachments
          </Tab>
        </TabList>
        <div style={{ padding: '1em' }}>
          {selectedTab === 'email' ? (
            <>
              <CustomField label={'Actions'}>
                <div style={{ display: 'flex', flexDirection: 'row' }}>
                  <SuccessButton
                    onClick={onClickSaveSingle}
                    tooltip={
                      'Save this preview only. (When processing multiple previews, switching between records will automatically save).'
                    }
                    tooltipProps={{ positioning: 'above' }}
                    iconProps={{ iconName: 'save' }}
                  >
                    Save Email
                  </SuccessButton>
                  <PrimaryButton
                    onClick={onClickSendSingle}
                    tooltip={'Save, then send this email only'}
                    tooltipProps={{ positioning: 'above' }}
                    iconProps={{ iconName: 'save' }}
                  >
                    Send Email
                  </PrimaryButton>
                  <DangerButton
                    onClick={onClickDeleteSingle}
                    tooltip={'Delete this email preview.'}
                    tooltipProps={{ positioning: 'above' }}
                  >
                    Delete
                  </DangerButton>
                </div>
              </CustomField>
              <Divider className={styles.divider} />
              <EmailDropdown dataid={'emailTo'} label={'To'} control={control} watch={watch} />
              <EmailDropdown dataid={'emailCc'} label={'Cc'} control={control} watch={watch} />
              <EmailTextField dataid={'emailSubject'} control={control} label={'Subject'} />
              <EmailWysiwygField dataid={'emailBody'} control={control} label={'Message'} watch={watch} />
            </>
          ) : (
            <Controller render={attachmentsRender} name={'attachments'} control={control} />
          )}
        </div>
      </div>
    );
  }
);

const useFluentTableStyles = makeStyles({
  approveCell: {
    maxWidth: '45px',
  },
  approvedMail: {
    color: p4Tokens.colorPaletteLightGreenForeground1,
  },
  errorMail: {
    color: p4Tokens.statusFlagDanger,
  },
});

const EmailActionCells = ({ item }) => {
  const fluentStyles = useFluentTableStyles();
  const focusableGroupAttr = useFocusableGroup({
    tabBehavior: 'limited-trap-focus',
  });
  const { resetSelection, previewRef } = useContext(EmailPrintPreviewContext);

  const handleQueryError = useComponentQueryErrorHandling();
  const [updateEmailPrintPreview, { loading }] = useMutation<
    IEmailPrintPreviewMutationResult,
    IEmailPrintPreviewMutationVariables
  >(EmailPrintPreviewMutation, {
    onError: handleQueryError,
    refetchQueries: [EmailPrintPreviewAggregateQuery, EmailPrintPreviewWorkspaceQuery],
  });

  const { dispatchSimpleAlert } = useP4ToastController();

  if (!item) return null;

  const isInvalid = !item.emailTo || item.emailTo.length === 0;
  let buttonIcon;
  let allowReadyToggle = true;
  if (loading) {
    allowReadyToggle = false;
    buttonIcon = <Spinner size={'tiny'} />;
  } else if (isInvalid) {
    allowReadyToggle = false;
    buttonIcon = (
      <Tooltip content={'There are no recipients set for this email preview - It cannot be sent.'} relationship="label">
        <MailErrorRegular className={fluentStyles.errorMail} />
      </Tooltip>
    );
  } else if (item?.ready) {
    buttonIcon = (
      <Tooltip content={'This email preview is ready to be sent.'} relationship="label">
        <MailCheckmarkRegular className={fluentStyles.approvedMail} />
      </Tooltip>
    );
  } else {
    buttonIcon = <MailRegular />;
  }

  return (
    <TableCell role="gridcell" tabIndex={0} {...focusableGroupAttr} className={fluentStyles.approveCell}>
      <TableCellLayout>
        <Button
          icon={buttonIcon}
          aria-label="Approve"
          disabled={!allowReadyToggle}
          onClick={(e) => {
            e.stopPropagation();
            if (!allowReadyToggle) return;
            const newReady = !item.ready;
            const currentPreview = previewRef?.current.getValues();
            const emailPrintPreview = currentPreview?.id === item?.id ? currentPreview : item;

            updateEmailPrintPreview({
              variables: {
                input: {
                  clientMutationId: getClientMutationId(),
                  emailPrintPreview: normalizeVariables({
                    ...emailPrintPreview,
                    ready: newReady,
                    //Covering a particular scenario where you're using the ready toggle while making edits.
                    //Unlikely for 2 users to conflict, should be safe to use the up-to-date list "version".
                    concurrencyVersion: item.concurrencyVersion,
                  }),
                },
              },
            }).then(() => {
              let text;
              if (emailPrintPreview.customer) {
                text = `Preview for customer ${emailPrintPreview.customer.name}`;
              } else if (emailPrintPreview.supplier) {
                text = `Preview for supplier ${emailPrintPreview.supplier.name}`;
              } else {
                text = 'Preview';
              }

              if (!newReady) {
                //When clearing the ready state, reset selection.
                //This is to avoid a particular niche-case where you have a record "Selected", you try to "Un-ready" it, but then navigate to another record
                // and trigger the 'record switch = make it ready' logic unintentionally.
                resetSelection?.();
              }

              dispatchSimpleAlert(
                'Preview Updated',
                newReady ? `${text} has been tagged as ready.` : `${text} ready state has been cleared`,
                newReady ? AlertType.Success : AlertType.Info
              );
            });
          }}
        />
      </TableCellLayout>
    </TableCell>
  );
};

const EmailPrintPreviewHeader = ({
  statusFlags,
  actionButtons: additionalActions,
  previewRef,
  isModal,
}: {
  statusFlags: IStatusFlag | Array<IStatusFlag>;
  actionButtons?: Array<IFormAction<any>>;
  previewRef: MutableRefObject<{ onSubmit: () => Promise<any> }>;
  isModal?: boolean;
}) => {
  const history = useHistory();
  const actionButtons = useEmailPrintPreviewActions(additionalActions, previewRef);

  const renderCancelButton = useCallback(
    (defaults: { onClick; 'data-testid': dataTestId }) => {
      const button = (
        <SecondaryButton data-testid={defaults['data-testid']} onClick={defaults.onClick} tabIndex={2} minWidth="80px">
          Close
        </SecondaryButton>
      );

      //When this modal is presented off the back of a document print, we dispatch it via the Toaster.
      //As such, to dismiss it, we need a <ToastTrigger> wrapper.
      if (isModal) {
        return <ToastTrigger>{button}</ToastTrigger>;
      }
      return button;
    },
    [isModal]
  );

  return (
    <HeaderBlock
      name="Email Print Preview"
      customBreadcrumb={[{ path: '/menu/Company', title: 'Company' }]}
      noSaveOption
      actionButtons={actionButtons}
      history={history}
      onCancel={() => {
        if (!isModal) {
          history.goBack();
        }
      }}
      hint={'Navigating between records on this form will save them as ready (to be sent).'}
      statusFlags={statusFlags}
      renderCancelButton={renderCancelButton}
    />
  );
};

type IEmailPrintPreviewFormProps = {
  //Filter the available previews displayed on the form. (I.e. for Modal viewing).
  predefinedFilters?: Array<IFilterInput>;
  //Indicate that we're displaying the form inside other content (I.e. a Modal).
  embeddedView?: boolean;
};

const EmailPrintPreviewForm = ({ predefinedFilters, embeddedView }: IEmailPrintPreviewFormProps) => {
  const infiniteLoaderRef = useRef<Parameters<typeof FluentTable>['infiniteLoaderRef']>();
  const previewRef = useRef<IPreviewEditRef>();

  const [remoteFilters, setRemoteFilters] = useState<Array<IFilterInput>>([]);
  const [seeAllUsers, setSeeAllUsers] = useState(false);

  const fullUserDetails = useSelector((store: IReduxStore) => store.app.fullUserDetails);
  const listFilters = useMemo(() => {
    const userFilters = seeAllUsers
      ? []
      : [
          {
            filterOperation: FilterOperation.Equals,
            filterParameters: {
              field: 'companyUser.id',
              value: `${fullUserDetails?.companyUser?.id}`,
            },
          },
        ];

    return [
      ...(predefinedFilters ?? []),
      ...remoteFilters,
      {
        filterOperation: FilterOperation.Equals,
        filterParameters: {
          field: 'processed',
          value: false,
        },
      },
      ...userFilters,
    ];
  }, [fullUserDetails?.companyUser?.id, predefinedFilters, remoteFilters, seeAllUsers]);

  const variables: IEmailPrintPreviewWorkspaceQueryVariables = useMemo(
    () => ({
      filters: listFilters,
      first: QUERY_PAGE_SIZE,
    }),
    [listFilters]
  );

  const { data, fetchMore } = useQuery<
    IEmailPrintPreviewWorkspaceQueryResult,
    IEmailPrintPreviewWorkspaceQueryVariables
  >(EmailPrintPreviewWorkspaceQuery, {
    variables: variables,
    errorPolicy: 'all',
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
    onCompleted: (data) => {
      //If something caused a reset (& thus, a re-fetch of the first page) then we need to reset the infinite loader cache in the table component.
      //Otherwise the reload will only fetch the first page.
      if (Object.keys(loadedPagesRef.current).length > 1 && data.listData?.pageInfo?.startCursor === '1') {
        loadedPagesRef.current = { 0: true };
        infiniteLoaderRef.current?.resetloadMoreItemsCache?.(true);
      }
    },
  });
  //Use a separate query for the aggregate 'ready' lookup (It can be done in the same query, but it results in us re-running the
  // aggregated queries for every page we fetch, resulting in a lot of wasted effort).

  const { data: aggregateData } = useQuery<
    IEmailPrintPreviewAggregateQueryResult,
    IEmailPrintPreviewAggregateQueryVariables
  >(EmailPrintPreviewAggregateQuery, {
    variables: {
      ...variables,
      filters: [
        ...variables.filters,
        { filterOperation: FilterOperation.Equals, filterParameters: { field: 'ready', value: 'true' } },
      ],
    },
    errorPolicy: 'all',
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
  });

  const items: Array<IEmailPrintPreview> = useMemo(
    () => data?.listData?.edges?.map((edge) => edge?.node as IEmailPrintPreview) ?? [],
    [data?.listData?.edges]
  );

  const readyCount = useMemo(() => {
    return aggregateData?.listData?.count ?? 0;
  }, [aggregateData?.listData?.count]);
  const [selectedPreview, setSelectedPreview] = useValueState<IEmailPrintPreview | undefined>(undefined);
  const selectedPreviews = useMemo(() => new Set([selectedPreview?.uuid as TableRowId]), [selectedPreview]);

  const tableFeatureOptions = useMemo(
    () => ({
      columns: EMAIL_PRINT_PREVIEW_COLUMNS,
      items,
    }),
    [items]
  );

  const loadedPagesRef = useRef<{ [key: number]: boolean }>({ 0: true });

  const isItemLoaded = useCallback((index: number) => {
    return loadedPagesRef.current[(index / QUERY_PAGE_SIZE) >> 0];
  }, []);
  const loadMoreItems: (startIndex: number, stopIndex: number) => Promise<void> | void = (startIndex, stopIndex) => {
    const startPage = (startIndex / QUERY_PAGE_SIZE) >> 0;
    const stopPage = (stopIndex / QUERY_PAGE_SIZE) >> 0;
    const promises: Array<Promise<ApolloQueryResult<any>>> = [];
    for (let i = startPage; i <= stopPage; i++) {
      if (!loadedPagesRef.current[i]) {
        promises.push(
          fetchMore({
            variables: {
              first: QUERY_PAGE_SIZE,
              after: i * QUERY_PAGE_SIZE,
            },
          })
        );
        loadedPagesRef.current[i] = true;
      }
    }
    return Promise.all(promises) as unknown as Promise<void>;
  };

  const containerRef = useRef();

  /**
   * Create a workspace context that mimics the list configuration for the email print preview form.
   */
  const workspaceContext = useMemo<Partial<WorkspaceContextType>>(() => {
    const workspaceSelectionState: IWorkspaceSelectionState = {
      selectedAll: true,
      actionEntities: [],
      filters: listFilters,
      count: data?.listData?.count ?? 0,
    };
    return {
      workspaceFilters: listFilters,
      selectionState: workspaceSelectionState,
      workspaceSelection: {
        getSelectionState: () => workspaceSelectionState,
      },
      embeddedView: embeddedView,
    };
  }, [data?.listData?.count, embeddedView, listFilters]);

  const emailPrintPreviewContext = useMemo<IEmailPrintPreviewContext>(() => {
    return {
      resetSelection: () => setSelectedPreview(undefined),
      previewRef: previewRef,
    };
  }, [setSelectedPreview]);
  let tableRowsHeight = 0;
  if (containerRef.current && containerRef.current.offsetHeight > 100) {
    tableRowsHeight = containerRef.current.offsetHeight - 66;
    tableRowsHeight = tableRowsHeight < 500 ? 500 : tableRowsHeight;
  }

  const actionButtons = useMemo(
    () => [
      {
        key: 'adminToggleSeeAllUsers',
        text: seeAllUsers ? 'See Own Previews Only' : 'See All Users Previews',
        onClick: () => {
          setSeeAllUsers(!seeAllUsers);
        },
        actionReference: EmailPrintPreviewAction.EmailPrintPreviewSendAll,
        condition: () => fullUserDetails?.systemUser?.admin,
        ignorePermissions: true,
      },
    ],
    [fullUserDetails?.systemUser?.admin, seeAllUsers]
  );

  return (
    <WorkspaceContext.Provider value={workspaceContext as WorkspaceContextType}>
      <EmailPrintPreviewContext.Provider value={emailPrintPreviewContext}>
        <Form.FormContainer>
          <EmailPrintPreviewHeader
            statusFlags={{
              key: 'rowCounter',
              override: true,
              status: [
                {
                  value: true,
                  style: StatusStyle.Success,
                  title: `Ready: ${readyCount}`,
                  icon: MailCheckmark16Regular,
                },
                {
                  value: true,
                  style: StatusStyle.Info,
                  title: `Total Previews: ${data?.listData?.count ?? 0}`,
                  icon: Mail16Regular,
                },
              ],
            }}
            actionButtons={actionButtons}
            previewRef={previewRef}
            isModal={embeddedView}
          />
          <div ref={containerRef} style={{ display: 'flex', flexDirection: 'row', height: '100%' }}>
            <div style={{ minWidth: '325px', width: '20%' }}>
              {tableRowsHeight > 0 ? (
                <FluentTable<IEmailPrintPreview>
                  itemCount={data?.listData?.count ?? 0}
                  height={tableRowsHeight}
                  tableFeatureOptions={tableFeatureOptions}
                  selectionMode={'single'}
                  selectionKey={'uuid'}
                  onSelectionChange={(e, data) => {
                    const selectedUuid = data.selectedItems.values()?.next()?.value;
                    const selectedView = items.find((value) => value.uuid === selectedUuid);
                    if (selectedView) {
                      setSelectedPreview(selectedView);
                    }
                  }}
                  selectedItems={selectedPreviews}
                  infiniteScrollConfig={{
                    isItemLoaded,
                    loadMoreItems,
                    setRemoteFilters,
                    remoteFilterGetter: genericRemoteFilterGetter,
                  }}
                  rowActionCells={EmailActionCells}
                  infiniteLoaderRef={infiniteLoaderRef}
                />
              ) : null}
            </div>
            <div style={{ width: '80%' }}>
              {selectedPreview ? (
                <PreviewEdit ref={previewRef} selectedPreview={selectedPreview} />
              ) : (
                <PreviewUnselected />
              )}
            </div>
          </div>
        </Form.FormContainer>
      </EmailPrintPreviewContext.Provider>
    </WorkspaceContext.Provider>
  );
};

export default EmailPrintPreviewForm;
